import * as React from "react";
import * as Headless from "@headlessui/react";
import { cn } from "@/lib/utils";
import { theme } from "@/lib/theme";
import { XMarkIcon } from "@heroicons/react/24/outline";

/**
 * Dialog component with design system integration
 * Built on @headlessui/react for accessibility
 * Supports multiple sizes and customization
 */

const sizes = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-xl",
  "2xl": "sm:max-w-2xl",
  "3xl": "sm:max-w-3xl",
  "4xl": "sm:max-w-4xl",
  full: "sm:max-w-[95vw]",
};

export interface DialogProps extends Omit<Headless.DialogProps, "as" | "className"> {
  size?: keyof typeof sizes;
  className?: string;
  children: React.ReactNode;
}

export function Dialog({ size = "lg", className, children, ...props }: DialogProps) {
  return (
    <Headless.Dialog {...props}>
      <Headless.DialogBackdrop
        transition
        data-testid="dialog-backdrop"
        className={cn(
          "fixed inset-0 z-[60] flex w-screen justify-center overflow-y-auto",
          "bg-background/80 backdrop-blur-sm",
          "px-2 py-2 sm:px-6 sm:py-8 lg:px-8 lg:py-16",
          theme.transition.DEFAULT,
          "data-[closed]:opacity-0",
          "data-[enter]:ease-out data-[leave]:ease-in",
        )}
      />

      <div className="fixed inset-0 z-[60] w-screen overflow-y-auto pt-6 sm:pt-0">
        <div className="grid min-h-full grid-rows-[1fr_auto] justify-items-center sm:grid-rows-[1fr_auto_3fr] sm:p-4">
          <Headless.DialogPanel
            transition
            data-testid="dialog-panel"
            className={cn(
              "row-start-2 w-full min-w-0",
              "rounded-t-3xl sm:rounded-2xl",
              "bg-card text-card-foreground",
              "p-8",
              theme.shadow.lg,
              "ring-1 ring-border",
              "sm:mb-auto",
              sizes[size],
              theme.transition.DEFAULT,
              "will-change-transform",
              "data-[closed]:translate-y-12 data-[closed]:opacity-0",
              "data-[enter]:ease-out data-[leave]:ease-in",
              "sm:data-[closed]:translate-y-0 sm:data-[closed]:data-[enter]:scale-95",
              className,
            )}
          >
            {children}
          </Headless.DialogPanel>
        </div>
      </div>
    </Headless.Dialog>
  );
}

export interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  onClose?: () => void;
}

export const DialogHeader = React.forwardRef<HTMLDivElement, DialogHeaderProps>(
  ({ className, children, onClose, ...props }, ref) => {
    return (
      <div ref={ref} className={cn("flex items-start justify-between", className)} {...props}>
        <div className="flex-1">{children}</div>
        {onClose && (
          <Headless.CloseButton
            className={cn(
              "ml-4 -mr-2 -mt-2 p-2",
              "rounded-md",
              "text-muted-foreground hover:text-foreground",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              theme.transition.DEFAULT,
            )}
            onClick={onClose}
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            <span className="sr-only">Close dialog</span>
          </Headless.CloseButton>
        )}
      </div>
    );
  },
);
DialogHeader.displayName = "DialogHeader";

export interface DialogTitleProps extends Omit<Headless.DialogTitleProps, "as" | "className"> {
  className?: string;
}

export function DialogTitle({ className, ...props }: DialogTitleProps) {
  return (
    <Headless.DialogTitle
      {...props}
      className={cn("text-lg font-semibold leading-6", "text-foreground", className)}
    />
  );
}

export interface DialogDescriptionProps extends Omit<
  Headless.DescriptionProps<"p">,
  "as" | "className"
> {
  className?: string;
}

export function DialogDescription({ className, ...props }: DialogDescriptionProps) {
  return (
    <Headless.Description
      as="p"
      {...props}
      className={cn("mt-2 text-sm text-muted-foreground", className)}
    />
  );
}

export interface DialogBodyProps extends React.HTMLAttributes<HTMLDivElement> {}

export const DialogBody = React.forwardRef<HTMLDivElement, DialogBodyProps>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={cn("mt-6", className)} {...props} />;
  },
);
DialogBody.displayName = "DialogBody";

export interface DialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

export const DialogFooter = React.forwardRef<HTMLDivElement, DialogFooterProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "mt-8 flex flex-col-reverse items-center justify-end gap-3",
          "*:w-full sm:flex-row sm:*:w-auto",
          className,
        )}
        {...props}
      />
    );
  },
);
DialogFooter.displayName = "DialogFooter";

// Simple wrapper for opening/closing dialog state
export function useDialog(defaultOpen = false) {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  const open = React.useCallback(() => setIsOpen(true), []);
  const close = React.useCallback(() => setIsOpen(false), []);
  const toggle = React.useCallback(() => setIsOpen((prev) => !prev), []);

  return {
    isOpen,
    setIsOpen,
    open,
    close,
    toggle,
  };
}
